import { ThemeEvents } from '@theme/events';

/** @type {Record<string, string>} */
const cachedRecommendations = {};

const DRAG_THRESHOLD = 8;
const CLICK_SUPPRESS_MS = 100;

class CartDrawerRecommendations extends HTMLElement {
  /** @type {AbortController | null} */
  #scrollDragController = null;

  /** @type {AbortController | null} */
  #activeDragSession = null;

  /** @type {IntersectionObserver} */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {string | undefined} */
  #storedAnchorProductId;

  /** @type {(event: Event) => void} */
  #onCartUpdate = () => {
    const newProductId = this.dataset.productId;

    if (newProductId === this.#storedAnchorProductId) return;

    this.#storedAnchorProductId = newProductId;
    delete this.dataset.recommendationsPerformed;

    if (!newProductId) return;

    if (this.getBoundingClientRect().height > 0) {
      this.#loadRecommendations();
      return;
    }

    this.#intersectionObserver.disconnect();
    this.#intersectionObserver.observe(this);
  };

  connectedCallback() {
    this.#storedAnchorProductId = this.dataset.productId;
    this.#intersectionObserver.observe(this);
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#initCarouselScroll();
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#activeFetch?.abort();
    this.#scrollDragController?.abort();
    this.#scrollDragController = null;
    this.#activeDragSession?.abort();
    this.#activeDragSession = null;
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  #initCarouselScroll() {
    const wrapper = this.querySelector('.cdr__scroll-wrapper');
    if (!wrapper || wrapper.dataset.scrollInit === 'true') return;

    const prevButton = this.querySelector('.cdr__nav--prev');
    const nextButton = this.querySelector('.cdr__nav--next');

    wrapper.dataset.scrollInit = 'true';
    this.#scrollDragController?.abort();
    this.#scrollDragController = new AbortController();
    const { signal } = this.#scrollDragController;

    const updateNav = () => {
      const canScroll = wrapper.scrollWidth > wrapper.clientWidth + 1;
      const atStart = wrapper.scrollLeft <= 2;
      const atEnd = wrapper.scrollLeft + wrapper.clientWidth >= wrapper.scrollWidth - 2;

      if (prevButton instanceof HTMLButtonElement) {
        prevButton.hidden = !canScroll || atStart;
      }

      if (nextButton instanceof HTMLButtonElement) {
        nextButton.hidden = !canScroll || atEnd;
      }
    };

    const getScrollStep = () => {
      const item = wrapper.querySelector('.cdr__item');
      const track = wrapper.querySelector('.cdr__track');
      if (!(item instanceof HTMLElement) || !(track instanceof HTMLElement)) {
        return wrapper.clientWidth * 0.75;
      }

      const gap = Number.parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 10;
      return item.offsetWidth + gap;
    };

    const scrollCarousel = (direction) => {
      wrapper.scrollBy({ left: direction * getScrollStep(), behavior: 'smooth' });
      window.setTimeout(updateNav, 350);
    };

    updateNav();
    wrapper.addEventListener('scroll', updateNav, { passive: true, signal });

    const resizeObserver = new ResizeObserver(updateNav);
    resizeObserver.observe(wrapper);
    signal.addEventListener('abort', () => resizeObserver.disconnect(), { once: true });

    if (prevButton instanceof HTMLButtonElement) {
      prevButton.addEventListener('click', () => scrollCarousel(-1), { signal });
    }

    if (nextButton instanceof HTMLButtonElement) {
      nextButton.addEventListener('click', () => scrollCarousel(1), { signal });
    }

    wrapper.addEventListener(
      'click',
      (event) => {
        if (wrapper.dataset.preventClick !== 'true') return;
        event.preventDefault();
        event.stopPropagation();
      },
      { capture: true, signal }
    );

    wrapper.addEventListener(
      'pointerdown',
      (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        if (wrapper.scrollWidth <= wrapper.clientWidth) return;

        this.#activeDragSession?.abort();
        this.#activeDragSession = new AbortController();
        const dragSignal = this.#activeDragSession.signal;

        const pointerId = event.pointerId;
        const startX = event.clientX;
        const startY = event.clientY;
        const startScrollLeft = wrapper.scrollLeft;
        let isDragging = false;
        let didDrag = false;

        /** @param {PointerEvent} moveEvent */
        const onPointerMove = (moveEvent) => {
          if (moveEvent.pointerId !== pointerId) return;

          const deltaX = moveEvent.clientX - startX;
          const deltaY = moveEvent.clientY - startY;

          if (!isDragging) {
            if (Math.abs(deltaX) <= DRAG_THRESHOLD || Math.abs(deltaX) <= Math.abs(deltaY)) {
              return;
            }

            isDragging = true;
            didDrag = true;
            wrapper.setPointerCapture(pointerId);
            wrapper.classList.add('is-dragging');
          }

          moveEvent.preventDefault();
          wrapper.scrollLeft = startScrollLeft - deltaX;
        };

        /** @param {PointerEvent} endEvent */
        const endDrag = (endEvent) => {
          if (endEvent.pointerId !== pointerId) return;

          if (isDragging) {
            try {
              wrapper.releasePointerCapture(pointerId);
            } catch {
              // Pointer may already be released.
            }
            wrapper.classList.remove('is-dragging');
          }

          this.#activeDragSession?.abort();
          this.#activeDragSession = null;

          if (didDrag) {
            wrapper.dataset.preventClick = 'true';
            window.setTimeout(() => {
              delete wrapper.dataset.preventClick;
            }, CLICK_SUPPRESS_MS);
          }

          updateNav();
        };

        document.addEventListener('pointermove', onPointerMove, { signal: dragSignal });
        document.addEventListener('pointerup', endDrag, { signal: dragSignal });
        document.addEventListener('pointercancel', endDrag, { signal: dragSignal });
      },
      { signal }
    );
  }

  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent, url } = this.dataset;

    if (!productId) return;

    if (recommendationsPerformed === 'true') return;

    this.#fetchCachedRecommendations(productId, sectionId, intent, url)
      .then((result) => {
        if (!result.success) return;

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector('#cart-drawer-recommendations');

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;
          this.#initCarouselScroll();
        }
      })
      .catch(() => {
        // Keep SSR collection fallback on error
      });
  }

  /**
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @param {string | undefined} baseUrl
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent, baseUrl) {
    const url = `${baseUrl}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }
}

if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
}
